import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import '../../styles/aida-base.css';
import '../../styles/aida-responsive.css';

/**
 * NonsoleMode Page
 * Displays the Playcast Nonsole Mode security information.
 * Route: /nonsole-mode
 */

const PURPLE_GRADIENT = [
  'linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,0.85) 10%, rgba(0,0,0,0.2) 25%, rgba(0,0,0,0.0) 40%, rgba(0,0,0,0.0) 60%, rgba(0,0,0,0.2) 75%, rgba(0,0,0,0.85) 90%, rgba(0,0,0,1) 100%)',
  'linear-gradient(90deg, rgba(120,40,255,0.55), rgba(180,60,255,0.55))',
  'radial-gradient(1400px at 50% 40%, rgba(255,255,255,0.03), rgba(60,0,90,0) 75%)',
  'radial-gradient(1000px at 15% 35%, rgba(140,40,255,0.40), rgba(40,0,70,0) 75%)',
  'radial-gradient(900px at 55% 60%, rgba(160,50,255,0.30), rgba(35,0,60,0) 80%)',
  'radial-gradient(1000px at 85% 70%, rgba(200,70,255,0.40), rgba(45,0,70,0) 80%)',
  'radial-gradient(circle at 50% 50%, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0.00) 60%)',
  'radial-gradient(circle at 50% 25%, #2a0050 0%, #1a0038 35%, #000000 75%, #000000 100%)',
].join(', ');

const PURPLE_BLEND = 'normal, soft-light, overlay, screen, screen, screen, soft-light, multiply';

const sectionHeading: React.CSSProperties = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 900,
  fontStyle: 'italic',
  fontSize: '36px',
  lineHeight: '46px',
  letterSpacing: '-0.3px',
  color: '#FBBF24',
  margin: '56px 0 20px',
  fontVariationSettings: "'wght' 900",
};

const subHeading: React.CSSProperties = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 700,
  fontSize: '24px',
  lineHeight: '34px',
  color: 'rgba(255,255,255,0.95)',
  margin: '32px 0 12px',
};

const paragraph: React.CSSProperties = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '20px',
  lineHeight: '34px',
  color: 'rgba(255,255,255,0.85)',
  margin: '0 0 20px',
};

const bulletList: React.CSSProperties = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: '20px',
  lineHeight: '34px',
  color: 'rgba(255,255,255,0.85)',
  margin: '12px 0 20px',
  paddingLeft: '32px',
  listStyleType: 'disc',
};

const bulletItem: React.CSSProperties = {
  margin: '6px 0',
};

export const NonsoleMode = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Nonsole Mode | Playcast</title>
        <meta name="description" content="Learn about Playcast Nonsole Mode — a dedicated security layer that keeps your PC safe while hosting game sessions." />
      </Helmet>

      <div
        className="w-full"
        style={{
          margin: 0,
          padding: 0,
          width: '100%',
          minHeight: '100vh',
          overflow: 'hidden',
          background: PURPLE_GRADIENT,
          backgroundBlendMode: PURPLE_BLEND,
        }}
      >
        <div style={{ paddingTop: '120px', display: 'flex', justifyContent: 'center' }}>
            <article style={{ width: '100%', maxWidth: '820px', padding: '40px 24px 80px' }}>
              {/* Title */}
              <h1 style={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 900,
                fontStyle: 'italic',
                fontSize: '48px',
                lineHeight: '58px',
                letterSpacing: '-0.5px',
                color: '#fff',
                margin: '0 0 72px',
                fontVariationSettings: "'wght' 900",
              }}>
                Playcast Nonsole Mode: <br />What You Need to Know
              </h1>

              <p style={{ ...paragraph, fontSize: '22px', lineHeight: '34px' }}>
                <strong>Hey Playcasters,</strong>
              </p>

              <p style={paragraph}>
                If you noticed a new user account appear on your Windows login screen after last night's update, that's the <strong>Playcast Nonsole account</strong> — and it's supposed to be there. Here's what it is and why it matters.
              </p>

              {/* What Is the Nonsole Account? */}
              <h2 style={sectionHeading}>What Is the Nonsole Account?</h2>
              <p style={paragraph}>
                The Nonsole account is a dedicated Windows user account that Playcast uses to remotely provision and run games on your PC while you're hosting other players. It is completely separate from your personal Windows account, and that separation is the point.
              </p>

              {/* Why Did We Create It? */}
              <h2 style={sectionHeading}>Why Did We Create It?</h2>
              <p style={paragraph}>
                Asking you to share your personal gaming PC with other players is a significant ask, and we take that responsibility seriously. Nonsole mode is one of several security layers designed to ensure that hosting never puts your data, privacy, or hardware at risk.
              </p>

              {/* Our Three Layers of Security */}
              <h2 style={sectionHeading}>Our Three Layers of Security</h2>

              <h3 style={subHeading}>1. Browser-Contained Streaming</h3>
              <p style={paragraph}>
                Games are streamed as video to a remote player's web browser. The player never has direct access to your PC — their entire experience is sandboxed within a video stream.
              </p>

              <h3 style={subHeading}>2. Trusted Publishers Only</h3>
              <p style={paragraph}>
                We only host games authorized by trusted publishers like Valve and Epic Games. All titles must originate from verified app stores.
              </p>

              <h3 style={subHeading}>3. Nonsole Mode — The Safety Net</h3>
              <p style={paragraph}>
                Even with the above protections, edge cases can happen — a game crash, for example, could ordinarily expose an open Windows desktop with remote input still active. Nonsole mode prevents that entirely:
              </p>
              <ul style={bulletList}>
                <li style={bulletItem}>It runs under a <strong>separate account</strong>, so your personal desktop is never visible.</li>
                <li style={bulletItem}>The <strong>Windows desktop is disabled</strong> in Nonsole mode — there's nothing underneath the game to expose.</li>
                <li style={bulletItem}>Network access, microphone, camera, file system access, and unknown services are all blocked.</li>
                <li style={bulletItem}><strong>Malware and virus scans</strong> run automatically during hosting sessions.</li>
                <li style={bulletItem}>Windows runs in a <strong>gamer-optimized mode</strong>, forcing the hosted game to maintain focus at all times.</li>
                <li style={bulletItem}>All remote inputs are <strong>encrypted and filtered</strong> — commands like Ctrl+Alt+Del are blocked at the network level before they can reach your system.</li>
                <li style={bulletItem}>We also employ additional security measures we keep confidential to protect against less obvious attack vectors.</li>
              </ul>

              {/* What Happens Next? */}
              <h2 style={sectionHeading}>What Happens Next?</h2>
              <p style={paragraph}>
                The appearance of the Nonsole account is the first step in a series of <strong>automated security tests</strong> we'll be running overnight to verify everything works as intended before our first gaming campaigns go live. We want to catch and resolve any issues before real players are ever involved.
              </p>
              <p style={paragraph}>
                Our security practices are also periodically reviewed by an <strong>independent cybersecurity expert</strong>, whose feedback is folded directly into our development cycle.
              </p>

              {/* One More Thing */}
              <h2 style={sectionHeading}>One More Thing</h2>
              <p style={{ ...paragraph, fontStyle: 'italic', fontSize: '21px', lineHeight: '34px' }}>
                When it's all said and done, the name says it all: <strong>Nonsole mode turns your PC into a fully contained game console experience — without requiring a second computer.</strong>
              </p>

              <p style={paragraph}>
                We're deeply grateful for your trust as we build this together. If you ever have concerns, you're always welcome to uninstall Playcast — no hard feelings. But we hope you'll stick with us as we launch something genuinely new in community-driven game publishing.
              </p>

              {/* Signature */}
              <div style={{ marginTop: '32px' }}>
                <p style={{ ...paragraph, margin: '0', fontStyle: 'italic' }}>Alex St. John</p>
                <p style={{ ...paragraph, margin: '0', fontStyle: 'italic' }}>Playcast CEO</p>
              </div>

              <p style={{ ...paragraph, marginTop: '24px', fontStyle: 'italic', opacity: 0.7 }}>
                Questions? Reach out to the Playcast team anytime.
              </p>
            </article>
        </div>
      </div>
    </>
  );
};
